/**
 * Feedback Dialog Component
 *
 * Allows users to submit feedback about issues through an in-app form
 * Collects feedback text and optional session context with user consent
 */

import {
  Component,
  inject,
  ChangeDetectionStrategy,
  signal,
  computed,
  OnDestroy,
} from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { FeedbackApiService } from '../../../api/feedback/feedback-api.service';
import { FeedbackRequest } from '../../../api/feedback/models';
import { AppConfigService } from '../../../root-services/app-config.service';
import {
  extractPageTypeFromUrl,
  extractSanitizedUrlFromString,
  extractSessionIdFromUrl,
} from '../../../core/utils/feedback-context.util';
import { extractErrorMessage } from '../../../core/utils/error-message-extractor.util';

@Component({
  selector: 'app-feedback-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatSnackBarModule,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="modern-dialog">
      <h2 class="dialog-title">
        {{ 'FEEDBACK.DIALOG_TITLE' | translate }}
      </h2>
      <div class="dialog-content">
        <form [formGroup]="form">
          <!-- Description Field -->
          <mat-form-field class="form-field" appearance="outline">
            <mat-label>{{
              'FEEDBACK.DESCRIPTION_LABEL' | translate
            }}</mat-label>
            <textarea
              matInput
              formControlName="description"
              [placeholder]="'FEEDBACK.DESCRIPTION_PLACEHOLDER' | translate"
              rows="6"
              cdkFocusInitial
            ></textarea>
            <mat-hint align="end">{{ characterCount() }}</mat-hint>
          </mat-form-field>

          <!-- Consent Checkbox -->
          <div class="consent-section">
            <mat-checkbox formControlName="consentChecked">
              {{ 'FEEDBACK.CONSENT_LABEL' | translate }}
            </mat-checkbox>
          </div>

          <!-- Error Display -->
          @if (error()) {
            <div class="error-message" role="alert" aria-live="assertive">
              <mat-icon class="error-icon" aria-hidden="true">error</mat-icon>
              <span>{{ error() }}</span>
            </div>
          }
        </form>
      </div>
      <div class="dialog-actions">
        <button mat-button (click)="onCancel()" class="secondary-button">
          {{ 'COMMON.CANCEL' | translate }}
        </button>
        <button
          mat-raised-button
          (click)="onSubmit()"
          class="primary-button"
          [disabled]="!form.valid || loading()"
        >
          @if (loading()) {
            <mat-spinner diameter="20" class="button-spinner"></mat-spinner>
          }
          {{ 'FEEDBACK.SUBMIT_BUTTON' | translate }}
        </button>
      </div>
    </div>
  `,
  styleUrl: './feedback-dialog.component.scss',
})
export class FeedbackDialogComponent implements OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<FeedbackDialogComponent>);
  private readonly feedbackApi = inject(FeedbackApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly appConfigService = inject(AppConfigService);

  private readonly destroy$ = new Subject<void>();

  loading = signal(false);
  error = signal<string | null>(null);
  private readonly descriptionValue = signal('');

  form = this.fb.group({
    description: ['', [Validators.required, Validators.maxLength(5000)]],
    consentChecked: [false],
  });

  characterCount = computed(() => {
    const text = this.descriptionValue();
    return this.translate.instant('FEEDBACK.CHARACTER_COUNT', {
      count: text.length,
    });
  });

  constructor() {
    // Subscribe to form changes to update signal
    this.form
      .get('description')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.descriptionValue.set(value || '');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit(): void {
    if (!this.form.valid || this.loading()) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.form.disable();

    const { description, consentChecked } = this.form.value;

    // Get current URL from browser location (more reliable than router.url)
    // router.url can be stale when location.replaceState() was used (e.g., new session creation)
    const currentUrl = window.location.pathname + window.location.search;

    // Always collect non-personal data using URL-based extraction
    const url = extractSanitizedUrlFromString(currentUrl);
    const pageType = extractPageTypeFromUrl(currentUrl);
    const appVersion =
      this.appConfigService.appConfig()?.appVersion ?? 'unknown';

    // Build request payload
    const request: FeedbackRequest = {
      text: description || '',
      user_consent: consentChecked || false,
      url,
      context: {
        page_type: pageType,
        app_version: appVersion,
      },
    };

    // Only include session_id and subscription_id if consent is checked
    if (consentChecked) {
      const sessionId = extractSessionIdFromUrl(currentUrl);
      if (sessionId) {
        request.session_id = sessionId;
      }

      const subscriptionId =
        this.route.snapshot.queryParamMap.get('subscription_id');
      if (subscriptionId) {
        request.subscription_id = subscriptionId;
      }
    }

    this.feedbackApi.submitFeedback(request).subscribe({
      next: () => {
        // Success: Show snackbar and close dialog
        this.snackBar.open(
          this.translate.instant('FEEDBACK.SUCCESS_MESSAGE'),
          '',
          {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: 'success-snackbar',
          },
        );
        this.dialogRef.close(true);
      },
      error: (err) => {
        // Error: Display in-dialog and re-enable form
        // Use centralized error extraction utility that supports:
        // - RFC 7807 Problem Details format
        // - Custom error codes
        // - FastAPI validation errors
        const errorMessage = extractErrorMessage(
          err,
          this.translate.instant('ERROR.GENERIC'),
        );

        this.error.set(errorMessage);
        this.loading.set(false);
        this.form.enable();
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
